import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ScheduleTimeTag } from '../../types/pointItemNote';
import { dateToWeekdayKey } from '../../utils/dashboardDateContext';
import {
  buildHalfHourCustomTimeTagLabelForRecord,
  dayKeyFromDate,
  formatScheduleTimeTagLabel,
  parseTimeTagLabelToScheduleTag,
  resolveCurrentScheduleTimeTag,
  splitScheduleTimeTagLabel,
} from '../../utils/pointItemScheduleTimeTags';
import CharacterEmptyState from '../common/CharacterEmptyState';
import GemStarIcon from './GemStarIcon';
import PointTypeIcon from './PointTypeIcon';
import PointItemDateCalendarModal from './PointItemDateCalendarModal';
import TimeTagSelectModal from './TimeTagSelectModal';

export type MemberHistoryMode = 'points' | 'coins' | 'notes';

export type MemberHistoryRow = {
  id: string;
  recordedDate: string;
  memo: string;
  timeTagLabel?: string;
  /** 포인트: 긍정/부정 별 아이콘 */
  pointCategory?: 'positive' | 'negative';
  /** 포인트 세트 커스텀 이모지 (없으면 기본 별) */
  pointEmoji?: string;
  /** 포인트/코인 숫자 값 (수정용) */
  value?: number;
  /** 포인트/코인 값 표시 (예: 1점, +10) */
  valueLabel?: string;
  /** 기록(노트) 유형 라벨 / 과제 상태 */
  typeLabel?: string;
  /** 코인: G=일반, L=레벨업 */
  coinTypeLabel?: 'G' | 'L';
  /** 과제 포인트 종류 */
  assignmentKind?: 'general' | 'project' | 'routine';
  /** 과제 포인트 등 읽기 전용 행 */
  readOnly?: boolean;
};

export type PointsHistorySubTab = 'assignment' | 'behavior';

export type MemberHistoryRecordEdit = {
  id: string;
  recordedDate: string;
  timeTagLabel?: string;
  memo: string;
  typeLabel?: string;
  pointCategory?: 'positive' | 'negative';
  value?: number;
  coinTypeLabel?: 'G' | 'L';
};

/** @deprecated 호환용 — MemberHistoryRecordEdit 사용 */
export type MemberHistoryNoteEdit = MemberHistoryRecordEdit;

export type MemberRecordHistoryModalProps = {
  open: boolean;
  onClose: () => void;
  memberName: string;
  title: string;
  mode: MemberHistoryMode;
  /** 행동 포인트 / 코인 / 노트 행 */
  rows: MemberHistoryRow[];
  /** 과제 포인트 행 (mode=points일 때 탭으로 표시) */
  assignmentRows?: MemberHistoryRow[];
  /** 날짜(YYYY-MM-DD) 기준 타임태그 목록 */
  getTimeTagsForDate?: (dateIso: string) => ScheduleTimeTag[];
  onDeleteRecords?: (ids: string[]) => void;
  onUpdateRecords?: (edits: MemberHistoryRecordEdit[]) => void;
  /** @deprecated onUpdateRecords 사용 */
  onUpdateNoteRecords?: (edits: MemberHistoryRecordEdit[]) => void;
};

function formatHistoryDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!m) return iso || '-';
  return `${m[2]}/${m[3]}`;
}

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function findTagByLabel(
  tags: ScheduleTimeTag[],
  label?: string
): ScheduleTimeTag | null {
  if (!label) return null;
  return tags.find((t) => formatScheduleTimeTagLabel(t) === label) ?? null;
}

function splitDayFromTimeTagLabel(label: string): { day?: string; body: string } {
  const trimmed = label.trim();
  const m = /^\[([일월화수목금토])\]\s*(.*)$/.exec(trimmed);
  if (!m) return { body: trimmed };
  return { day: m[1], body: (m[2] || '').trim() || trimmed };
}

function NoteTimeTagCell({ label }: { label?: string }) {
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(null);
  const { name, timeRange } = splitScheduleTimeTagLabel(label || '');

  if (!label) return <>-</>;

  const { day, body } = splitDayFromTimeTagLabel(name || label);
  const displayName = day ? body : name || label;

  const showTip = (el: HTMLElement) => {
    if (!timeRange) return;
    const rect = el.getBoundingClientRect();
    setTipPos({
      left: rect.left + rect.width / 2,
      top: rect.top - 8,
    });
  };

  return (
    <span
      className="cp-pin-history-timetag"
      onMouseEnter={(e) => showTip(e.currentTarget)}
      onMouseLeave={() => setTipPos(null)}
    >
      {day ? (
        <span className="cp-pin-history-timetag-day" aria-hidden>
          {day}
        </span>
      ) : null}
      <span className="cp-pin-history-timetag-name">{displayName}</span>
      {timeRange && tipPos
        ? createPortal(
            <span
              className="cp-pin-history-timetag-tip"
              role="tooltip"
              style={{ left: tipPos.left, top: tipPos.top }}
            >
              {timeRange}
            </span>,
            document.body
          )
        : null}
    </span>
  );
}

const NOTE_TYPES = [
  { value: '일반', label: '일반 기록', tone: 'general' },
  { value: '학습', label: '학습 기록', tone: 'study' },
  { value: '행동', label: '행동 기록', tone: 'behavior' },
] as const;

const HISTORY_PAGE_SIZE = 20;

const DAY_KO: Record<string, string> = {
  sun: '일',
  mon: '월',
  tue: '화',
  wed: '수',
  thu: '목',
  fri: '금',
  sat: '토',
};

const MemberRecordHistoryModal: React.FC<MemberRecordHistoryModalProps> = ({
  open,
  onClose,
  memberName,
  title,
  mode,
  rows,
  assignmentRows = [],
  getTimeTagsForDate,
  onDeleteRecords,
  onUpdateRecords,
  onUpdateNoteRecords,
}) => {
  const applyUpdates = onUpdateRecords ?? onUpdateNoteRecords;
  const [query, setQuery] = useState('');
  const [pointsSubTab, setPointsSubTab] = useState<PointsHistorySubTab>('assignment');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(() => new Set());
  const [pendingEdits, setPendingEdits] = useState<Record<string, MemberHistoryRecordEdit>>(
    {}
  );
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<MemberHistoryRecordEdit | null>(null);
  const [editSelectedTag, setEditSelectedTag] = useState<ScheduleTimeTag | null>(null);
  const [editDateCalOpen, setEditDateCalOpen] = useState(false);
  const [editTimeTagOpen, setEditTimeTagOpen] = useState(false);
  const [editDateField, setEditDateField] = useState<'year' | 'month' | 'day' | null>(null);
  const [editDateTemp, setEditDateTemp] = useState('');

  const resetEditUi = () => {
    setEditingId(null);
    setEditDraft(null);
    setEditSelectedTag(null);
    setEditDateCalOpen(false);
    setEditTimeTagOpen(false);
    setEditDateField(null);
    setEditDateTemp('');
  };

  const isAssignmentTab = mode === 'points' && pointsSubTab === 'assignment';
  const sourceRows = isAssignmentTab ? assignmentRows : rows;

  const assignmentPointsTotal = useMemo(
    () =>
      assignmentRows.reduce((sum, r) => sum + (typeof r.value === 'number' ? r.value : 0), 0),
    [assignmentRows]
  );

  const behaviorPointsTotals = useMemo(() => {
    let positive = 0;
    let negative = 0;
    rows.forEach((r) => {
      const v = typeof r.value === 'number' ? r.value : 0;
      if (r.pointCategory === 'negative' || v < 0) {
        negative += Math.abs(v);
      } else {
        positive += Math.abs(v);
      }
    });
    return { positive, negative };
  }, [rows]);

  const toIsoFromParts = (y: number, m: number, d: number): string =>
    `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIds(new Set());
      setPendingDeleteIds(new Set());
      setPendingEdits({});
      setPointsSubTab('assignment');
      setPage(1);
      resetEditUi();
    }
  }, [open]);

  useEffect(() => {
    setPage(1);
  }, [query, pointsSubTab, mode]);

  useEffect(() => {
    const valid = new Set(rows.map((r) => r.id));
    setPendingDeleteIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => {
        if (valid.has(id)) next.add(id);
      });
      return next.size !== prev.size ? next : prev;
    });
    setPendingEdits((prev) => {
      const next: Record<string, MemberHistoryRecordEdit> = {};
      Object.entries(prev).forEach(([id, edit]) => {
        if (valid.has(id)) next[id] = edit;
      });
      return Object.keys(next).length !== Object.keys(prev).length ? next : prev;
    });
  }, [rows]);

  useEffect(() => {
    const valid = new Set(sourceRows.map((r) => r.id));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => {
        if (valid.has(id)) next.add(id);
      });
      return next.size !== prev.size ? next : prev;
    });
  }, [sourceRows]);

  const displayRows = useMemo(() => {
    if (isAssignmentTab) return assignmentRows;
    return rows.map((r) => {
      const edit = pendingEdits[r.id];
      if (!edit) return r;
      const next: MemberHistoryRow = {
        ...r,
        recordedDate: edit.recordedDate,
        timeTagLabel: edit.timeTagLabel,
        memo: edit.memo,
      };
      if (mode === 'notes') {
        next.typeLabel = edit.typeLabel || r.typeLabel;
      } else if (mode === 'points') {
        next.pointCategory = edit.pointCategory ?? r.pointCategory;
        if (edit.value != null) {
          next.value = edit.value;
          next.valueLabel = `${edit.value}점`;
        }
      } else if (mode === 'coins') {
        next.coinTypeLabel = edit.coinTypeLabel ?? r.coinTypeLabel;
        if (edit.value != null) {
          next.value = edit.value;
          next.valueLabel = `${edit.value > 0 ? '+' : ''}${edit.value}`;
        }
      }
      return next;
    });
  }, [isAssignmentTab, assignmentRows, rows, pendingEdits, mode]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return displayRows;
    return displayRows.filter((r) => (r.memo || '').toLowerCase().includes(q));
  }, [displayRows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / HISTORY_PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * HISTORY_PAGE_SIZE;
    return filtered.slice(start, start + HISTORY_PAGE_SIZE);
  }, [filtered, safePage]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const valueHeader = mode === 'coins' ? '코인' : '포인트';
  const showValueCol = mode !== 'notes';
  const canMultiSelect = !isAssignmentTab;
  const hasPendingEdits = Object.keys(pendingEdits).length > 0;
  const canSave =
    (pendingDeleteIds.size > 0 && !!onDeleteRecords) ||
    (hasPendingEdits && !!applyUpdates);

  const editDate = useMemo(
    () => (editDraft ? parseIsoDate(editDraft.recordedDate) : new Date()),
    [editDraft]
  );

  const editDayTags = useMemo(() => {
    if (!editDraft || !getTimeTagsForDate) return [];
    return getTimeTagsForDate(editDraft.recordedDate);
  }, [editDraft, getTimeTagsForDate]);

  const editDayKey =
    editSelectedTag?.dayKey ||
    dateToWeekdayKey(editDate) ||
    dayKeyFromDate(editDate);
  const editDayLabel =
    DAY_KO[editDayKey] ??
    ['일', '월', '화', '수', '목', '금', '토'][editDate.getDay()] ??
    '';

  /** 수업시수 매칭이 없어도 라벨(`[일] 8시`)에서 커스텀 태그 복원 */
  const editEffectiveTag = useMemo(() => {
    if (editSelectedTag) return editSelectedTag;
    if (!editDraft?.timeTagLabel) return null;
    return parseTimeTagLabelToScheduleTag(editDraft.timeTagLabel, editDayKey);
  }, [editSelectedTag, editDraft?.timeTagLabel, editDayKey]);

  const editTitle =
    mode === 'coins'
      ? '코인 기록 수정'
      : mode === 'notes'
        ? '노트 기록 수정'
        : '포인트 기록 수정';

  const switchPointsSubTab = (tab: PointsHistorySubTab) => {
    setPointsSubTab(tab);
    setSelectedIds(new Set());
    setQuery('');
    setPage(1);
    resetEditUi();
  };

  const toggleSelect = (id: string) => {
    if (editingId || isAssignmentTab) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageSelectableIds = useMemo(
    () => pagedRows.filter((r) => !r.readOnly && canMultiSelect).map((r) => r.id),
    [pagedRows, canMultiSelect]
  );

  const allPageSelected =
    pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selectedIds.has(id));

  const toggleSelectAllOnPage = () => {
    if (!canMultiSelect || editingId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageSelectableIds.forEach((id) => next.delete(id));
      } else {
        pageSelectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const markPendingDelete = (id: string) => {
    if (isAssignmentTab) return;
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const markSelectedPendingDelete = () => {
    if (!canMultiSelect || selectedIds.size === 0) return;
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      selectedIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const openEdit = (row: MemberHistoryRow) => {
    if (row.readOnly || isAssignmentTab) return;
    const recordedDate = row.recordedDate || new Date().toISOString().slice(0, 10);
    const tags = getTimeTagsForDate?.(recordedDate) ?? [];
    const dayKey = dayKeyFromDate(parseIsoDate(recordedDate));
    const matched =
      findTagByLabel(tags, row.timeTagLabel) ??
      parseTimeTagLabelToScheduleTag(row.timeTagLabel, dayKey);
    setEditingId(row.id);
    setEditDraft({
      id: row.id,
      recordedDate,
      timeTagLabel: row.timeTagLabel || '',
      memo: row.memo || '',
      typeLabel: row.typeLabel || '일반',
      pointCategory: row.pointCategory || 'positive',
      value: row.value ?? 0,
      coinTypeLabel: row.coinTypeLabel || 'G',
    });
    setEditSelectedTag(matched);
    setEditDateCalOpen(false);
    setEditTimeTagOpen(false);
    setSelectedIds(new Set([row.id]));
  };

  const applyEditDate = (iso: string) => {
    const tags = getTimeTagsForDate?.(iso) ?? [];
    const date = parseIsoDate(iso);
    const now = new Date();
    const dayKey = dayKeyFromDate(date);
    const resolved = resolveCurrentScheduleTimeTag(tags, isSameLocalDay(date, now) ? now : date);
    const fallback = buildHalfHourCustomTimeTagLabelForRecord({
      recordedDate: iso,
      createdAt: now,
    });
    const nextLabel = resolved ? formatScheduleTimeTagLabel(resolved) : fallback;
    setEditDraft((prev) =>
      prev
        ? {
            ...prev,
            recordedDate: iso,
            timeTagLabel: nextLabel,
          }
        : prev
    );
    setEditSelectedTag(
      resolved ?? parseTimeTagLabelToScheduleTag(fallback, dayKey)
    );
    setEditDateCalOpen(false);
    setEditDateField(null);
    setEditDateTemp('');
  };

  const beginEditDatePart = (field: 'year' | 'month' | 'day') => {
    setEditDateField(field);
    if (field === 'year') setEditDateTemp(String(editDate.getFullYear()));
    else if (field === 'month') setEditDateTemp(String(editDate.getMonth() + 1));
    else setEditDateTemp(String(editDate.getDate()));
  };

  const commitEditDatePart = (field: 'year' | 'month' | 'day') => {
    const value = Number.parseInt(editDateTemp, 10);
    if (!Number.isFinite(value)) {
      setEditDateField(null);
      setEditDateTemp('');
      return;
    }
    let y = editDate.getFullYear();
    let m = editDate.getMonth() + 1;
    let d = editDate.getDate();
    if (field === 'year') {
      if (value < 2000 || value > 2100) {
        setEditDateField(null);
        setEditDateTemp('');
        return;
      }
      y = value;
    } else if (field === 'month') {
      if (value < 1 || value > 12) {
        setEditDateField(null);
        setEditDateTemp('');
        return;
      }
      m = value;
    } else if (value < 1 || value > 31) {
      setEditDateField(null);
      setEditDateTemp('');
      return;
    } else {
      d = value;
    }
    const next = new Date(y, m - 1, d);
    if (
      next.getFullYear() === y &&
      next.getMonth() === m - 1 &&
      next.getDate() === d
    ) {
      applyEditDate(toIsoFromParts(y, m, d));
      return;
    }
    setEditDateField(null);
    setEditDateTemp('');
  };

  const confirmEdit = () => {
    if (!editDraft) return;
    setPendingEdits((prev) => ({
      ...prev,
      [editDraft.id]: {
        ...editDraft,
        timeTagLabel: editDraft.timeTagLabel?.trim() || undefined,
      },
    }));
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.delete(editDraft.id);
      return next;
    });
    resetEditUi();
  };

  const cancelEdit = () => {
    resetEditUi();
  };

  const handleSave = () => {
    if (!canSave) return;
    const ids = Array.from(pendingDeleteIds);
    const edits = Object.values(pendingEdits).filter((e) => !pendingDeleteIds.has(e.id));
    if (ids.length) onDeleteRecords?.(ids);
    if (edits.length) applyUpdates?.(edits);
    setPendingDeleteIds(new Set());
    setPendingEdits({});
    setSelectedIds(new Set());
    resetEditUi();
  };

  if (!open) return null;
  return createPortal(
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget && !editingId) onClose();
      }}
    >
      <div
        className="cp-modal cp-pin-history-modal has-timetag"
        data-tutorial-pin="history-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-history-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="cp-pin-history-header">
          <h3 id="cp-pin-history-title">
            {title}
            <span className="cp-pin-history-member">{memberName}</span>
          </h3>
          <button
            type="button"
            className="cp-pin-history-close"
            aria-label="닫기"
            onClick={onClose}
          >
            <svg
              className="cp-pin-history-close-icon"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="cp-modal-body">
          {mode === 'points' ? (
            <div className="cp-pin-history-subtabs" role="tablist" aria-label="포인트 구분">
              <button
                type="button"
                role="tab"
                aria-selected={pointsSubTab === 'assignment'}
                className={`cp-pin-history-subtab${
                  pointsSubTab === 'assignment' ? ' is-active' : ''
                }`}
                onClick={() => switchPointsSubTab('assignment')}
              >
                <span className="cp-pin-history-subtab-label">과제 포인트</span>
                <span
                  className="cp-pin-history-subtab-tip is-positive"
                  aria-label={`과제 포인트 ${assignmentPointsTotal}점`}
                >
                  {assignmentPointsTotal}
                </span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={pointsSubTab === 'behavior'}
                className={`cp-pin-history-subtab${
                  pointsSubTab === 'behavior' ? ' is-active' : ''
                }`}
                onClick={() => switchPointsSubTab('behavior')}
              >
                <span className="cp-pin-history-subtab-label">행동 포인트</span>
                <span className="cp-pin-history-subtab-tips">
                  <span
                    className="cp-pin-history-subtab-tip is-positive"
                    aria-label={`긍정행동 포인트 ${behaviorPointsTotals.positive}점`}
                  >
                    {behaviorPointsTotals.positive}
                  </span>
                  <span
                    className="cp-pin-history-subtab-tip is-negative"
                    aria-label={`부정행동 포인트 ${behaviorPointsTotals.negative}점`}
                  >
                    {behaviorPointsTotals.negative}
                  </span>
                </span>
              </button>
            </div>
          ) : null}

          <div className="cp-pin-history-toolbar">
            <input
              className="cp-pin-history-search"
              type="search"
              placeholder={isAssignmentTab ? '과제명 검색...' : '메모 검색...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="button"
              className="cp-pin-history-search-btn"
              aria-label="검색"
              onClick={() => undefined}
            >
              <svg
                className="cp-pin-history-search-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2.2" />
                <path
                  d="M20 20L16.5 16.5"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {canMultiSelect && onDeleteRecords ? (
              <button
                type="button"
                className="cp-pin-history-bulk-delete-btn"
                disabled={selectedIds.size === 0}
                title={
                  selectedIds.size === 0
                    ? '기록을 선택하면 일괄 삭제할 수 있습니다'
                    : `선택한 ${selectedIds.size}건을 임시 삭제로 표시`
                }
                onClick={markSelectedPendingDelete}
              >
                삭제{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </button>
            ) : null}
          </div>

          {filtered.length === 0 ? (
            <CharacterEmptyState
              variant="modal"
              message={
                sourceRows.length === 0
                  ? isAssignmentTab
                    ? '과제에서 부여된 포인트 기록이 없어요!'
                    : mode === 'points'
                      ? '포인트 기록이 없어요!'
                      : mode === 'coins'
                        ? '코인 기록이 없어요!'
                        : '기록이 없어요!'
                  : '검색 결과가 없어요!'
              }
            />
          ) : (
            <>
            <div className="cp-pin-history-table-wrap">
              <table className="cp-pin-history-table">
                <thead>
                  <tr>
                    {canMultiSelect ? (
                      <th className="cp-pin-history-col-select" scope="col">
                        <button
                          type="button"
                          className={
                            allPageSelected
                              ? 'cp-pin-history-select-circle is-checked'
                              : 'cp-pin-history-select-circle'
                          }
                          aria-pressed={allPageSelected}
                          aria-label={allPageSelected ? '이 페이지 선택 해제' : '이 페이지 전체 선택'}
                          disabled={pageSelectableIds.length === 0 || !!editingId}
                          onClick={toggleSelectAllOnPage}
                        />
                      </th>
                    ) : null}
                    <th className="cp-pin-history-col-type">타입</th>
                    <th className="cp-pin-history-col-date">날짜</th>
                    <th className="cp-pin-history-col-timetag">
                      {isAssignmentTab ? '상태' : '타임태그'}
                    </th>
                    <th className="cp-pin-history-col-memo">
                      {isAssignmentTab ? '과제' : '메모'}
                    </th>
                    {showValueCol ? (
                      <th className="cp-pin-history-col-value">{valueHeader}</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row) => {
                    const selected = selectedIds.has(row.id);
                    const pendingDelete = pendingDeleteIds.has(row.id);
                    const pendingEdit = !!pendingEdits[row.id];
                    const selectable = canMultiSelect && !row.readOnly;
                    const showActions =
                      selectable && (selected || pendingDelete || pendingEdit);
                    return (
                      <tr
                        key={row.id}
                        className={[
                          selected ? 'is-selected' : '',
                          pendingDelete ? 'is-pending-delete' : '',
                          pendingEdit && !pendingDelete ? 'is-pending-edit' : '',
                          isAssignmentTab || row.readOnly ? 'is-readonly' : '',
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined}
                        onClick={() => {
                          if (selectable) toggleSelect(row.id);
                        }}
                      >
                        {canMultiSelect ? (
                          <td className="cp-pin-history-col-select">
                            {selectable ? (
                              <button
                                type="button"
                                className={
                                  selected
                                    ? 'cp-pin-history-select-circle is-checked'
                                    : 'cp-pin-history-select-circle'
                                }
                                aria-pressed={selected}
                                aria-label="기록 선택"
                                disabled={!!editingId}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSelect(row.id);
                                }}
                              />
                            ) : null}
                          </td>
                        ) : null}
                        <td className="cp-pin-history-col-type">
                          {showActions ? (
                            <div className="cp-pin-history-row-actions">
                              <button
                                type="button"
                                className={`cp-pin-history-row-trash${
                                  pendingDelete ? ' is-pending' : ''
                                }`}
                                aria-label={
                                  pendingDelete ? '임시 삭제 취소' : '임시 삭제로 표시'
                                }
                                title={
                                  pendingDelete
                                    ? '다시 누르면 임시 삭제가 취소됩니다'
                                    : '임시 삭제로 표시 (저장 시 반영)'
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markPendingDelete(row.id);
                                }}
                              >
                                <img
                                  src="/del.svg"
                                  alt=""
                                  className="cp-pin-history-trash-icon"
                                  width={14}
                                  height={14}
                                  draggable={false}
                                />
                              </button>
                              <button
                                type="button"
                                className="cp-pin-history-row-edit"
                                aria-label="기록 수정"
                                title="기록 수정"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEdit(row);
                                }}
                              >
                                <img
                                  src="/edit.svg"
                                  alt=""
                                  className="cp-pin-history-edit-icon"
                                  width={14}
                                  height={14}
                                  draggable={false}
                                />
                              </button>
                            </div>
                          ) : isAssignmentTab || row.assignmentKind ? (
                            <span
                              className={`cp-pin-history-asgn-kind is-${
                                row.assignmentKind || 'general'
                              }`}
                            >
                              {row.assignmentKind === 'project'
                                ? '프로젝트'
                                : row.assignmentKind === 'routine'
                                  ? '루틴'
                                  : '일반'}
                            </span>
                          ) : mode === 'points' ? (
                            <PointTypeIcon
                              positive={row.pointCategory !== 'negative'}
                              emoji={row.pointEmoji}
                              size={18}
                            />
                          ) : mode === 'coins' ? (
                            <span
                              className={`cp-pin-history-coin-type${
                                row.coinTypeLabel === 'L' ? ' is-levelup' : ' is-general'
                              }`}
                              title={
                                row.coinTypeLabel === 'L' ? '레벨업 보상' : '일반 보상'
                              }
                            >
                              {row.coinTypeLabel === 'L' ? 'L' : 'G'}
                            </span>
                          ) : (
                            <span
                              className={`cp-pin-history-note-type${
                                row.typeLabel === '학습'
                                  ? ' is-study'
                                  : row.typeLabel === '행동'
                                    ? ' is-behavior'
                                    : ' is-general'
                              }`}
                            >
                              {row.typeLabel || '일반'}
                            </span>
                          )}
                        </td>
                        <td className="cp-pin-history-col-date">
                          <div className="cp-pin-history-date">
                            {formatHistoryDate(row.recordedDate)}
                          </div>
                        </td>
                        <td className="cp-pin-history-col-timetag">
                          {isAssignmentTab ? (
                            <span className="cp-pin-history-asgn-status">
                              {row.typeLabel || '-'}
                            </span>
                          ) : (
                            <NoteTimeTagCell label={row.timeTagLabel} />
                          )}
                        </td>
                        <td className="cp-pin-history-col-memo cp-pin-history-memo">
                          {row.memo || '-'}
                        </td>
                        {showValueCol ? (
                          <td className="cp-pin-history-col-value">{row.valueLabel || '-'}</td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length > 0 ? (
              <div className="cp-pin-history-pager" role="navigation" aria-label="기록 페이지">
                <button
                  type="button"
                  className="cp-pin-history-pager-btn"
                  disabled={safePage <= 1}
                  aria-label="이전 페이지"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  &lt;
                </button>
                <span className="cp-pin-history-pager-status">
                  {safePage} / {totalPages}
                  <span className="cp-pin-history-pager-count">({filtered.length}건)</span>
                </span>
                <button
                  type="button"
                  className="cp-pin-history-pager-btn"
                  disabled={safePage >= totalPages}
                  aria-label="다음 페이지"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  &gt;
                </button>
              </div>
            ) : null}
            </>
          )}
        </div>

        {isAssignmentTab ? null : (
          <div className="cp-pin-history-footer">
            <button
              type="button"
              className="cp-pin-history-footer-btn is-save"
              disabled={!canSave}
              onClick={handleSave}
            >
              저장
            </button>
          </div>
        )}

        {editingId && editDraft ? (
          <div className="cp-pin-history-edit-overlay" role="presentation">
            <div
              className="cp-pin-history-edit-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-pin-history-edit-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <h4 id="cp-pin-history-edit-title">{editTitle}</h4>

              <div className="cp-pin-history-edit-meta-row">
                <div className="cp-pin-history-edit-date-chip" title="날짜">
                  <button
                    type="button"
                    className="cp-pin-history-edit-date-cal-btn"
                    onClick={() => setEditDateCalOpen(true)}
                    title="달력에서 날짜 선택"
                    aria-label="달력에서 날짜 선택"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="16"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      />
                      <path
                        d="M3 10h18M8 3v4M16 3v4"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      />
                    </svg>
                  </button>
                  <span className="cp-pin-history-edit-date-text">
                    <span className="cp-pin-history-edit-date-year-row">
                      {editDateField === 'year' ? (
                        <input
                          className="cp-pin-history-edit-date-input"
                          value={editDateTemp}
                          onChange={(e) => setEditDateTemp(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEditDatePart('year');
                            if (e.key === 'Escape') {
                              setEditDateField(null);
                              setEditDateTemp('');
                            }
                          }}
                          onBlur={() => commitEditDatePart('year')}
                          autoFocus
                          aria-label="연도"
                        />
                      ) : (
                        <button
                          type="button"
                          className="cp-pin-history-edit-date-part"
                          onClick={() => beginEditDatePart('year')}
                        >
                          {editDate.getFullYear()}년
                        </button>
                      )}
                    </span>
                    <span className="cp-pin-history-edit-date-md-row">
                      {editDateField === 'month' ? (
                        <input
                          className="cp-pin-history-edit-date-input"
                          value={editDateTemp}
                          onChange={(e) => setEditDateTemp(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEditDatePart('month');
                            if (e.key === 'Escape') {
                              setEditDateField(null);
                              setEditDateTemp('');
                            }
                          }}
                          onBlur={() => commitEditDatePart('month')}
                          autoFocus
                          aria-label="월"
                        />
                      ) : (
                        <button
                          type="button"
                          className="cp-pin-history-edit-date-part"
                          onClick={() => beginEditDatePart('month')}
                        >
                          {editDate.getMonth() + 1}월
                        </button>
                      )}
                      {editDateField === 'day' ? (
                        <input
                          className="cp-pin-history-edit-date-input"
                          value={editDateTemp}
                          onChange={(e) => setEditDateTemp(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEditDatePart('day');
                            if (e.key === 'Escape') {
                              setEditDateField(null);
                              setEditDateTemp('');
                            }
                          }}
                          onBlur={() => commitEditDatePart('day')}
                          autoFocus
                          aria-label="일"
                        />
                      ) : (
                        <button
                          type="button"
                          className="cp-pin-history-edit-date-part"
                          onClick={() => beginEditDatePart('day')}
                        >
                          {editDate.getDate()}일
                        </button>
                      )}
                    </span>
                  </span>
                </div>

                <button
                  type="button"
                  className="cp-pin-history-edit-timetag-chip"
                  onClick={() => {
                    if (!editSelectedTag && editEffectiveTag) {
                      setEditSelectedTag(editEffectiveTag);
                    }
                    setEditTimeTagOpen(true);
                  }}
                  title="타임 태그 선택"
                >
                  <span className="cp-pin-history-edit-timetag-icon" aria-hidden>
                    #
                  </span>
                  <span className="cp-pin-history-edit-timetag-text">
                    <strong>{editEffectiveTag?.name ?? '타임 태그'}</strong>
                    <small>
                      {editEffectiveTag
                        ? `${editEffectiveTag.startTime} ~ ${editEffectiveTag.endTime}`
                        : editDraft.timeTagLabel
                          ? splitScheduleTimeTagLabel(editDraft.timeTagLabel).name ||
                            '선택됨'
                          : '선택 안됨'}
                    </small>
                  </span>
                </button>
              </div>

              {mode === 'notes' ? (
                <div className="cp-pin-history-edit-types" role="group" aria-label="기록 유형">
                  {NOTE_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      className={`cp-pin-history-edit-type is-${t.tone}${
                        editDraft.typeLabel === t.value ? ' is-active' : ''
                      }`}
                      aria-pressed={editDraft.typeLabel === t.value}
                      onClick={() =>
                        setEditDraft((prev) =>
                          prev ? { ...prev, typeLabel: t.value } : prev
                        )
                      }
                    >
                      <span className={`cp-pin-history-edit-type-dot is-${t.tone}`} />
                      {t.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {mode === 'points' ? (
                <div
                  className="cp-pin-history-edit-types is-two"
                  role="group"
                  aria-label="포인트 유형"
                >
                  <button
                    type="button"
                    className={`cp-pin-history-edit-type is-general${
                      editDraft.pointCategory !== 'negative' ? ' is-active' : ''
                    }`}
                    aria-pressed={editDraft.pointCategory !== 'negative'}
                    onClick={() =>
                      setEditDraft((prev) =>
                        prev ? { ...prev, pointCategory: 'positive' } : prev
                      )
                    }
                  >
                    <GemStarIcon positive size={16} />
                    긍정
                  </button>
                  <button
                    type="button"
                    className={`cp-pin-history-edit-type is-behavior${
                      editDraft.pointCategory === 'negative' ? ' is-active' : ''
                    }`}
                    aria-pressed={editDraft.pointCategory === 'negative'}
                    onClick={() =>
                      setEditDraft((prev) =>
                        prev ? { ...prev, pointCategory: 'negative' } : prev
                      )
                    }
                  >
                    <GemStarIcon positive={false} size={16} />
                    부정
                  </button>
                </div>
              ) : null}

              {mode === 'coins' ? (
                <div
                  className="cp-pin-history-edit-types is-two"
                  role="group"
                  aria-label="코인 유형 (변경 불가)"
                >
                  <button
                    type="button"
                    className={`cp-pin-history-edit-type is-general is-locked${
                      editDraft.coinTypeLabel !== 'L' ? ' is-active' : ''
                    }`}
                    aria-pressed={editDraft.coinTypeLabel !== 'L'}
                    disabled
                    title="코인 유형은 변경할 수 없습니다"
                  >
                    <span className="cp-pin-history-coin-type is-general">G</span>
                    일반
                  </button>
                  <button
                    type="button"
                    className={`cp-pin-history-edit-type is-study is-locked${
                      editDraft.coinTypeLabel === 'L' ? ' is-active' : ''
                    }`}
                    aria-pressed={editDraft.coinTypeLabel === 'L'}
                    disabled
                    title="코인 유형은 변경할 수 없습니다"
                  >
                    <span className="cp-pin-history-coin-type is-levelup">L</span>
                    레벨업
                  </button>
                </div>
              ) : null}

              {mode !== 'notes' ? (
                <label className="cp-pin-history-edit-value">
                  <span>{mode === 'coins' ? '코인' : '포인트'}</span>
                  <input
                    type="number"
                    value={editDraft.value ?? 0}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setEditDraft((prev) =>
                        prev
                          ? {
                              ...prev,
                              value: Number.isFinite(n) ? n : 0,
                            }
                          : prev
                      );
                    }}
                  />
                </label>
              ) : null}

              <textarea
                className="cp-pin-history-edit-memo"
                rows={5}
                placeholder="메모를 입력하세요"
                value={editDraft.memo}
                onChange={(e) =>
                  setEditDraft((prev) => (prev ? { ...prev, memo: e.target.value } : prev))
                }
              />
              {mode === 'notes' ? (
                <p className="cp-pin-history-edit-memo-hint" role="note">
                  주민번호·연락처·주소 등 민감한 개인정보는 입력하지 않도록 주의해 주세요.
                </p>
              ) : null}

              <div className="cp-pin-history-edit-actions">
                <button type="button" className="cp-btn-secondary" onClick={cancelEdit}>
                  취소
                </button>
                <button type="button" className="cp-btn-primary" onClick={confirmEdit}>
                  확인
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <PointItemDateCalendarModal
        open={editDateCalOpen}
        selectedDateIso={editDraft?.recordedDate || ''}
        onClose={() => setEditDateCalOpen(false)}
        onSelect={applyEditDate}
      />
      <TimeTagSelectModal
        open={editTimeTagOpen}
        onClose={() => setEditTimeTagOpen(false)}
        tags={editDayTags}
        selected={editEffectiveTag}
        labelHint={editDraft?.timeTagLabel}
        dayLabel={editDayLabel}
        dayKey={editDayKey}
        customPrefill="selected"
        onSelect={(tag) => {
          if (tag) {
            setEditSelectedTag(tag);
            setEditDraft((prev) =>
              prev ? { ...prev, timeTagLabel: formatScheduleTimeTagLabel(tag) } : prev
            );
            return;
          }
          const label = buildHalfHourCustomTimeTagLabelForRecord({
            recordedDate: editDraft?.recordedDate,
            createdAt: new Date(),
          });
          const parsed = parseTimeTagLabelToScheduleTag(label, dayKeyFromDate(editDate));
          setEditSelectedTag(parsed);
          setEditDraft((prev) => (prev ? { ...prev, timeTagLabel: label } : prev));
        }}
      />
    </div>,
    document.body
  );
};

export default MemberRecordHistoryModal;
